import { Box, Flex, Text } from '@chakra-ui/react'
import { useRef, useState } from 'react'
import useKeyPress from 'utils/useKeyPress'
import styled from 'styled-components'
import moment from 'moment'
import { DateRangePicker } from 'react-dates'
import { Select } from 'components/Select'
import './styles.css'
import {
  MonthYearSelect,
  RenderPresets,
} from 'components/Dashboards/IDATrialsMapDashboard/DatePicker'
import { FiltersT, getCountryName } from '..'

export type FilterType = any

const HiddenInput = styled.div`
  button {
    outline: none;
  }

  .SingleDatePickerInput {
    .DateInput {
      display: none;
    }
    .SingleDatePickerInput_calendarIcon {
      padding: 0;
    }
  }
  .DateRangePickerInput {
    .DateInput {
      display: none;
    }
    .DateRangePickerInput_calendarIcon {
      padding: 0;
    }
    background: none;
    outline: none;
  }
  .DateRangePickerInput_arrow {
    display: none;
  }
`

const ShowTopCountryOptions = [1, 2, 5, 10, 15, 20, 'All']

const DatePickerTrigger = ({
  startDate,
  endDate,
}: {
  startDate: string | null | undefined
  endDate: string | null | undefined
}) => {
  const start = moment(startDate).format('MMM Do YYYY')
  const end = moment(endDate).format('MMM Do YYYY')
  return (
    <Flex
      bg='white'
      className='dateRange'
      border={'1px'}
      borderColor={'gray.200'}
      h={8}
      position={'relative'}
      px={2}
      alignItems={'center'}
      py={1}
      borderRadius={'sm'}
    >
      {startDate || endDate ? (
        <Text fontSize={'xs'} color={'gray.800'} fontWeight={'semibold'}>
          {start} - {end}
        </Text>
      ) : (
        <Text fontSize={'xs'} color={'gray.800'} fontWeight={'semibold'}>
          Time Range
        </Text>
      )}
    </Flex>
  )
}

export const DateFilter = ({
  dates,
  onDateChange,
  disease,
}: {
  dates: { start: string; end: string }
  onDateChange: (start: string, end: string) => void
  disease?: string
}) => {
  // keep moment in state to match DateRangePicker API
  const [startDate, setStartDate] = useState<moment.Moment | null>(
    moment(dates.start)
  )
  const [endDate, setEndDate] = useState<moment.Moment | null>(
    moment(dates.end)
  )
  const [focused, setFocused] = useState<'startDate' | 'endDate' | null>(null)

  // when true, the next onClose is caused by a preset and should not fire onDateChange again
  const skipNextOnClose = useRef(false)

  const emitChange = (
    start: moment.Moment | null,
    end: moment.Moment | null
  ) => {
    if (!start || !end) return
    onDateChange(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'))
  }

  useKeyPress('Escape', () => {
    if (focused) setFocused(null)
  })

  return (
    <Flex>
      <Box style={{ zIndex: 99 }}>
        <HiddenInput>
          <DateRangePicker
            hideKeyboardShortcutsPanel
            startDateId='startDatePicker'
            endDateId='endDatePicker'
            withPortal
            renderMonthElement={(props) => <MonthYearSelect {...props} />}
            inputIconPosition='before'
            anchorDirection='right'
            isOutsideRange={() => false}
            startDate={startDate}
            endDate={endDate}
            focusedInput={focused}
            onFocusChange={setFocused}
            onDatesChange={({ startDate, endDate }) => {
              setStartDate(startDate ?? null)
              setEndDate(endDate ?? null)
            }}
            onClose={({ startDate, endDate }) => {
              if (skipNextOnClose.current) {
                // consume the skip just once
                skipNextOnClose.current = false
                return
              }
              emitChange(startDate ?? null, endDate ?? null)
            }}
            renderCalendarInfo={() => (
              <RenderPresets
                disease={disease}
                onPresetClick={({ startDate, endDate }) => {
                  // mark that the next onClose is due to a preset click
                  skipNextOnClose.current = true

                  setStartDate(startDate ?? null)
                  setEndDate(endDate ?? null)

                  // emit change immediately for the preset selection
                  if (startDate && endDate) {
                    emitChange(startDate, endDate)
                  }

                  // optionally close the picker to make it feel instant
                  setFocused(null)
                }}
              />
            )}
            customInputIcon={
              <DatePickerTrigger startDate={dates.start} endDate={dates.end} />
            }
          />
        </HiddenInput>
      </Box>
    </Flex>
  )
}
const MainFilterSection = ({
  countries,
  filters,
  handleFilters,
  handleCountry,
  isSingleType,
  disease,
}: {
  countries: string[]
  handleFilters: (filters: FiltersT) => void
  handleCountry: (country: string) => void
  filters: FiltersT
  isSingleType?: string
  disease?: string
}) => {
  const isBotulism = disease === 'Botulism'
  function onDataTypeChange(e: any) {
    handleFilters({ ...filters, dataType: e.value })
  }
  function onNCountriesChange(e: any) {
    if (e.value === 'All') {
      handleFilters({ ...filters, nCountries: countries?.length })
    } else {
      handleFilters({ ...filters, nCountries: e.value })
    }
  }

  function onCountryChange(e: any) {
    if (e.value === 'global') {
      handleCountry(filters.country)
    }
    handleCountry(e.value)
  }
  function onDateChange(start: string, end: string) {
    handleFilters({ ...filters, dateRange: { start, end } })
  }

  const countryOptions = [
    { value: 'global', label: 'Global' },
    ...countries
      .sort((a, b) => {
        const countryA = getCountryName(a)
        const countryB = getCountryName(b)
        return countryA.localeCompare(countryB)
      })
      .map((item) => ({
        value: item ?? '',
        label: getCountryName(item ?? ''),
      })),
  ]

  return (
    <Flex
      h='44px'
      w='full'
      border={'1px'}
      borderColor={'gray.200'}
      bg='gray.100'
      px={3}
      py={1}
      borderRadius={'md'}
      justifyContent={'space-between'}
      fontSize={'sm'}
    >
      <Flex alignItems={'center'}>
        <Box>{disease}</Box>
        {!isSingleType ? (
          <Box textTransform='capitalize' mx='8px'>
            <Select
              value={{ value: filters?.dataType, label: filters?.dataType }}
              options={[
                { value: 'infections', label: 'infections' },
                { value: 'deaths', label: 'deaths' },
              ]}
              onChange={onDataTypeChange}
              isMulti={false}
              isSearchable={false}
              backgroundColor='#fff'
              color='#000'
              fontSize={'14px'}
              height='26px'
            />
          </Box>
        ) : (
          <Text mx={2}>{isSingleType}</Text>
        )}
        <Text>over</Text>
        <Box>
          <DateFilter
            dates={filters.dateRange}
            onDateChange={onDateChange}
            disease={disease}
          />
        </Box>
        <Text>in</Text>
        <Box textTransform='capitalize' mx='8px'>
          <Select
            value={{
              value: filters?.country,
              label: getCountryName(filters?.country) || filters?.country,
            }}
            options={countryOptions}
            onChange={onCountryChange}
            isMulti={false}
            isSearchable={true}
            backgroundColor='#fff'
            color='#000'
            fontSize={'14px'}
            height='26px'
          />
        </Box>
      </Flex>
      <Flex alignItems={'center'} fontSize={'14px'}>
        <Text>Show Top </Text>
        <Box mt={-1} mx={2} textTransform='capitalize'>
          <Select
            value={{ value: filters.nCountries, label: filters.nCountries }}
            isMulti={false}
            options={ShowTopCountryOptions.map((option) => {
              return { value: option, label: option }
            })}
            isSearchable={false}
            backgroundColor='#fff'
            color='#000'
            onChange={onNCountriesChange}
            fontSize={'14px'}
            height='26px'
          />
        </Box>
        <Text>{filters.country === 'global' ? 'Countries' : 'Regions'}</Text>
      </Flex>
    </Flex>
  )
}

export default MainFilterSection
